import { FileWarning, SearchX } from 'lucide-react';
import { useState, type ReactElement } from 'react';

import { avatarColorIndex, nameInitials } from '@/components/ui/avatar';
import { cn } from '@/components/ui/cn';
import { InlineError } from '@/components/ui/StatePanel';
import { ApiError } from '@/lib/api';
import { useActiveCompany } from '@/lib/active-company-context';

import { assignedLabel, positionsLabel, readerBadge } from '../presentation';
import type { Reader, TrainingRow } from '../types';
import { useDocumentReaders } from '../use-training';
import { CompetencyList } from './CompetencyList';
import { DispatchRetry } from './DispatchRetry';
import { SKELETON_CYCLE, skeletonLine } from '@/components/ui/skeleton';
import { errorAdvice } from '@/lib/error-advice';

function ReaderRow({ entryId, reader }: { entryId: string; reader: Reader }): ReactElement {
  const [open, setOpen] = useState(false);
  const badge = readerBadge(reader);
  const panelId = `comp-${entryId}-${reader.lmsUserId}`;
  return (
    <>
      <button
        type="button"
        className="reader"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={cn('av sm', `p${avatarColorIndex(`lms:${reader.lmsUserId}`)}`)}
          aria-hidden="true"
        >
          {nameInitials(reader.name)}
        </span>
        <span className="who-txt">
          <span className="nm" style={{ display: 'block' }}>
            {reader.name}
          </span>
          <span className="rl" style={{ display: 'block' }}>
            {positionsLabel(reader.positions)}
          </span>
        </span>
        <span className={cn('badge', badge.cls)}>{badge.label}</span>
      </button>
      {open && <CompetencyList entryId={entryId} lmsUserId={reader.lmsUserId} id={panelId} />}
    </>
  );
}

/** Detalle de un documento (mockup 06): quién leyó y quién sigue pendiente de la
 *  versión vigente. Tocar una persona despliega sus competencias del LMS. */
export function DocumentDetail({
  entryId,
  code,
  assigned,
  dispatch,
  onReset,
}: {
  entryId: string;
  code: string | null;
  assigned: number | null;
  /** Último envío de la capacitación del documento (aviso y reintento si falló). */
  dispatch: TrainingRow['dispatch'];
  /** Vuelve a la selección por defecto (documento de la URL inexistente). */
  onReset: () => void;
}): ReactElement {
  const { activeCompanyId } = useActiveCompany();
  const query = useDocumentReaders(entryId, {
    companyId: activeCompanyId,
    enabled: Boolean(activeCompanyId),
  });
  const title = code ?? query.data?.entry.code ?? 'Documento';
  const count = assigned ?? query.data?.readers.length ?? null;

  return (
    <div className="card rise d3">
      <div className="ch">
        <h3>{title} · detalle</h3>
        {count !== null && <span className="count">{assignedLabel(count)}</span>}
      </div>
      <DispatchRetry entryId={entryId} code={title} dispatch={dispatch} />
      {query.isLoading ? (
        <div className="cb" aria-busy="true" aria-label="Cargando el detalle">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className={skeletonLine({
                width: SKELETON_CYCLE[i % SKELETON_CYCLE.length],
                ...(i > 0 ? { space: 'top' } : {}),
              })}
            />
          ))}
        </div>
      ) : query.error instanceof ApiError && query.error.status === 404 ? (
        <div className="cb rowb" style={{ gap: 12 }} role="alert">
          <span aria-hidden="true" style={{ color: 'var(--ink-faint)', display: 'flex' }}>
            <SearchX />
          </span>
          <div>
            <h4 style={{ fontSize: 13.5, marginBottom: 2 }}>No encontramos este documento</h4>
            <p className="soft" style={{ fontSize: 12.5, margin: 0 }}>
              No está vigente, no tiene lecturas en el LMS o no pertenece a la empresa activa.
            </p>
          </div>
          <button
            type="button"
            className="btn mut sm"
            style={{ marginLeft: 'auto' }}
            onClick={onReset}
          >
            Ver el primero de la lista
          </button>
        </div>
      ) : query.isError ? (
        <InlineError icon={<FileWarning />} onRetry={() => void query.refetch()}>
          No se pudo cargar el detalle del documento. {errorAdvice(query.error)}
        </InlineError>
      ) : (query.data?.readers ?? []).length === 0 ? (
        <div className="cb">
          <p className="soft" style={{ fontSize: 13, margin: 0 }}>
            Nadie tiene asignada la lectura de la versión vigente en la empresa activa.
          </p>
        </div>
      ) : (
        <div className="readers">
          {(query.data?.readers ?? []).map((r) => (
            <ReaderRow key={r.lmsUserId} entryId={entryId} reader={r} />
          ))}
          {query.data?.truncated && (
            <p className="foot-note">
              Se muestran los primeros 1000 lectores, empezando por los pendientes.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
